import { useState, useRef, useCallback, useEffect } from 'react';
import type { BrokenCondition } from '@/features/questionnaires/utils/checkQuestionChangeConditions';
import type { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import type { QuestionnaireFormData } from '@/features/questionnaires/types/questionnaire.interface';

export type WarningType =
  | 'reorder'
  | 'typeChange'
  | 'optionChange'
  | 'deletion';

type WarningDialogState = {
  open: boolean;
  type: WarningType;
  brokenConditions: BrokenCondition[];
  onConfirm:
    | ((action: 'cancel' | 'proceed' | 'proceedAndClear') => void)
    | null;
};

type PendingOperations = {
  reorder: { fromIndex: number; toIndex: number } | null;
  deletion: number | null;
  change: (() => void) | null;
};

type UseQuestionWarningDialogProps = {
  getValues: UseFormGetValues<QuestionnaireFormData>;
  setValue: UseFormSetValue<QuestionnaireFormData>;
  move: (from: number, to: number) => void;
  remove: (index: number) => void;
};

export const useQuestionWarningDialog = ({
  getValues,
  setValue,
  move,
  remove,
}: UseQuestionWarningDialogProps) => {
  const [warningState, setWarningState] = useState<WarningDialogState>({
    open: false,
    type: 'reorder',
    brokenConditions: [],
    onConfirm: null,
  });

  const [pendingOperations, setPendingOperations] = useState<PendingOperations>(
    {
      reorder: null,
      deletion: null,
      change: null,
    },
  );

  const pendingChangeRef = useRef<(() => void) | null>(null);
  const pendingOperationsRef = useRef<PendingOperations>(pendingOperations);
  const brokenConditionsRef = useRef<BrokenCondition[]>([]);

  // Keep ref in sync with state
  useEffect(() => {
    pendingOperationsRef.current = pendingOperations;
  }, [pendingOperations]);

  const clearBrokenConditions = useCallback(
    (brokenConditions: BrokenCondition[]) => {
      const currentQuestions = getValues('questions');

      // Group broken conditions by questionId and collect their IDs
      const brokenConditionsByQuestion = new Map<string, Set<string>>();
      brokenConditions.forEach(({ questionId, condition }) => {
        const existingSet =
          brokenConditionsByQuestion.get(questionId) || new Set<string>();
        existingSet.add(condition.id);
        brokenConditionsByQuestion.set(questionId, existingSet);
      });

      brokenConditionsByQuestion.forEach((brokenConditionIds, questionId) => {
        const questionIndex = currentQuestions.findIndex(
          (q) => q.id === questionId,
        );
        if (questionIndex !== -1) {
          const question = currentQuestions[questionIndex];
          if (question.displayConditions?.conditions) {
            // Filter out conditions with IDs that are in the broken list
            const validConditions =
              question.displayConditions.conditions.filter(
                (condition) => !brokenConditionIds.has(condition.id),
              );

            if (validConditions.length > 0) {
              setValue(`questions.${questionIndex}.displayConditions`, {
                ...question.displayConditions,
                conditions: validConditions,
              });
            } else {
              setValue(`questions.${questionIndex}.displayConditions`, null);
            }
          }
        }
      });
    },
    [getValues, setValue],
  );

  const showWarning = useCallback(
    (
      type: WarningType,
      brokenConditions: BrokenCondition[],
      onConfirm: (action: 'cancel' | 'proceed' | 'proceedAndClear') => void,
    ) => {
      brokenConditionsRef.current = brokenConditions;
      setWarningState({
        open: true,
        type,
        brokenConditions,
        onConfirm,
      });
    },
    [],
  );

  const closeWarning = useCallback(() => {
    setWarningState({
      open: false,
      type: warningState.type,
      brokenConditions: [],
      onConfirm: null,
    });
  }, [warningState.type]);

  const handleReorderConfirm = useCallback(
    (action: 'cancel' | 'proceed' | 'proceedAndClear') => {
      const currentPendingOperations = pendingOperationsRef.current;

      // Capture broken conditions before any state changes
      const brokenConditionsToClean = [...brokenConditionsRef.current];

      if (
        currentPendingOperations.reorder &&
        (action === 'proceed' || action === 'proceedAndClear')
      ) {
        move(
          currentPendingOperations.reorder.fromIndex,
          currentPendingOperations.reorder.toIndex,
        );
        if (action === 'proceedAndClear') {
          // Use setTimeout to ensure the reorder has been applied before clearing conditions
          setTimeout(() => {
            clearBrokenConditions(brokenConditionsToClean);
          }, 0);
        }
      }
      setPendingOperations((prev) => ({ ...prev, reorder: null }));
      closeWarning();
    },
    [move, clearBrokenConditions, closeWarning],
  );

  const handleDeletionConfirm = useCallback(
    (action: 'cancel' | 'proceed' | 'proceedAndClear') => {
      const currentPendingOperations = pendingOperationsRef.current;

      // Capture broken conditions before any state changes
      const brokenConditionsToClean = [...brokenConditionsRef.current];

      if (
        currentPendingOperations.deletion !== null &&
        (action === 'proceed' || action === 'proceedAndClear')
      ) {
        remove(currentPendingOperations.deletion);
        if (action === 'proceedAndClear') {
          // Use setTimeout to ensure the deletion has been applied before clearing conditions
          setTimeout(() => {
            clearBrokenConditions(brokenConditionsToClean);
          }, 0);
        }
      }
      setPendingOperations((prev) => ({ ...prev, deletion: null }));
      closeWarning();
    },
    [remove, clearBrokenConditions, closeWarning],
  );

  const handleChangeConfirm = useCallback(
    (action: 'cancel' | 'proceed' | 'proceedAndClear') => {
      if (action === 'cancel') {
        pendingChangeRef.current = null;
        closeWarning();
        return;
      }

      // Capture broken conditions before any state changes
      const brokenConditionsToClean = [...brokenConditionsRef.current];

      if (pendingChangeRef.current) {
        pendingChangeRef.current();
      }

      if (action === 'proceedAndClear') {
        // Use setTimeout to ensure the change has been applied before clearing conditions
        setTimeout(() => {
          clearBrokenConditions(brokenConditionsToClean);
        }, 0);
      }

      pendingChangeRef.current = null;
      closeWarning();
    },
    [clearBrokenConditions, closeWarning],
  );

  const setPendingReorder = useCallback(
    (fromIndex: number, toIndex: number) => {
      setPendingOperations((prev) => ({
        ...prev,
        reorder: { fromIndex, toIndex },
      }));
    },
    [],
  );

  const setPendingDeletion = useCallback((index: number) => {
    setPendingOperations((prev) => ({ ...prev, deletion: index }));
  }, []);

  const setPendingChange = useCallback((onChange: () => void) => {
    pendingChangeRef.current = onChange;
  }, []);

  return {
    warningState,
    showWarning,
    closeWarning,
    handleReorderConfirm,
    handleDeletionConfirm,
    handleChangeConfirm,
    setPendingReorder,
    setPendingDeletion,
    setPendingChange,
  };
};
